import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import ChildrenManagementContent from '../../../../components/child/ChildrenManagementContent';
import DataOps from '../../../../data/DataOps';
import { Child } from '../../../../interface/Child';

interface FamilyMembersSectionProps {
    isEditing: boolean;
    user: any;
    activeChild: any;
    setActiveChild: (child: any) => void;
    getMembersSectionTitle: () => string;
    getMembersSubtitle: () => string;
    getEmptyStateText: () => { icon: any; title: string; text: string; buttonText?: string };
}

const FamilyMembersSection: React.FC<FamilyMembersSectionProps> = ({
    user,
    getMembersSectionTitle,
    getMembersSubtitle,
}) => {
    const [children, setChildren] = useState<Child[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Load children from the `child` collection on mount
    useEffect(() => {
        if (!user?.userId) return;
        let cancelled = false;

        (async () => {
            try {
                const fetched = await DataOps.fetchChildren(user.userId);
                if (!cancelled) setChildren(fetched as Child[]);
            } catch (e) {
                console.error('Error fetching children:', e);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [user?.userId]);

    const handleSaveChild = async (child: Child) => {
        await DataOps.saveChild(child);
    };

    const handleDeleteChild = async (child: Child) => {
        await DataOps.deleteChild(child.userId);
    };

    return (
        <>
            <View style={styles.dividerContainer}>
                <View style={styles.dividerLine}>
                    <View style={styles.dividerGlow} />
                </View>
            </View>

            <Text style={styles.sectionTitle}>{getMembersSectionTitle()}</Text>
            <Text style={styles.sectionSubtitle}>{getMembersSubtitle()}</Text>

            <View style={styles.contentWrapper}>
                <ChildrenManagementContent
                    children={children}
                    onChildrenChange={setChildren}
                    onSaveChild={handleSaveChild}
                    onDeleteChild={handleDeleteChild}
                    isLoading={isLoading}
                    parentId={user?.userId || ''}
                    parentFirstName={user?.firstName || ''}
                    parentLastName={user?.lastName || ''}
                    parentPhone={user?.phone || user?.email || ''}
                />
            </View>
        </>
    );
};

export default FamilyMembersSection;

const styles = StyleSheet.create({
    dividerContainer: {
        alignItems: 'center',
        marginVertical: 48,
        paddingHorizontal: 40,
    },
    dividerLine: {
        width: '100%',
        height: 1,
        backgroundColor: '#F1F5F9',
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
    },
    dividerGlow: {
        width: 60,
        height: 4,
        backgroundColor: '#3C8248',
        borderRadius: 2,
        position: 'absolute',
    },
    sectionTitle: {
        fontSize: 24,
        fontWeight: '900',
        color: '#1E293B',
        marginBottom: 8,
        textAlign: 'center',
        letterSpacing: -1,
    },
    sectionSubtitle: {
        fontSize: 15,
        color: '#64748B',
        textAlign: 'center',
        maxWidth: 400,
        alignSelf: 'center',
        lineHeight: 22,
        marginBottom: 24,
    },
    contentWrapper: {
        width: '100%',
        maxWidth: 600,
        alignSelf: 'center',
    },
});
